import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { nearMissService } from '../../../services/nearMissService';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuth } from '../../../contexts/AuthContext';
import { companyService } from '../../../services/companyService';
import NotesFeed from '../../../components/ui/NotesFeed';

const STATUS_OPTIONS = ['Open', 'Under review', 'Closed'];

const NearMissDetailModal = ({ nearMiss, isOpen, onClose, onStatusUpdated }) => {
  const { t } = useLanguage();
  const { userProfile } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [status, setStatus] = useState(nearMiss?.status || 'Open');
  const [updating, setUpdating] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    if (isOpen && nearMiss?.id && !nearMiss?._demo) {
      loadPhotos();
    }
    setStatus(nearMiss?.status || 'Open');
  }, [isOpen, nearMiss?.id]);

  useEffect(() => {
    companyService?.getUserCompany()?.then(result => {
      if (result?.data?.id) setCompanyId(result?.data?.id);
    });
  }, []);

  const loadPhotos = async () => {
    setPhotosLoading(true);
    const { data } = await nearMissService?.getNearMissPhotos(nearMiss?.id);
    setPhotos(data || []);
    setPhotosLoading(false);
  };

  const handleStatusUpdate = async () => {
    if (status === nearMiss?.status) return;
    setUpdating(true);
    const { error } = await nearMissService?.updateNearMissStatus(nearMiss?.id, status);
    if (error) {
      toast?.error(error || 'Failed to update status');
    } else {
      toast?.success('Status updated');
      onStatusUpdated?.({ ...nearMiss, status });
    }
    setUpdating(false);
  };

  if (!isOpen || !nearMiss) return null;

  const riskColors = {
    High: 'bg-red-100 text-red-700 border-red-200',
    Medium: 'bg-amber-100 text-amber-700 border-amber-200',
    Low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };

  const statusColors = {
    Open: 'bg-red-100 text-red-700',
    'Under review': 'bg-amber-100 text-amber-700',
    Closed: 'bg-emerald-100 text-emerald-700',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground font-heading">{t('nearMissDetailsTitle')}</h2>
              <p className="text-xs text-muted-foreground">
                {nearMiss?.date ? new Date(nearMiss?.date)?.toLocaleDateString('en-GB') : '—'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-6">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'details' ? 'border-accent text-accent' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'notes' ? 'border-accent text-accent' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            <Icon name="MessageSquare" size={14} />
            Notes
          </button>
        </div>

        <div className="p-6 space-y-5">
          {activeTab === 'details' && (
            <>
              {/* Meta row */}
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${riskColors?.[nearMiss?.risk_level] || 'bg-muted text-muted-foreground border-border'}`}>
                  <Icon name="AlertTriangle" size={12} />
                  {nearMiss?.risk_level} {t('nearMissRiskLabel')}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${statusColors?.[nearMiss?.status] || 'bg-muted text-muted-foreground'}`}>
                  {nearMiss?.status}
                </span>
                {nearMiss?.site?.site_name && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                    <Icon name="Building2" size={12} />
                    {nearMiss?.site?.site_name}
                  </span>
                )}
              </div>

              {/* Location */}
              {nearMiss?.location && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('nearMissColLocation')}</p>
                  <p className="text-sm text-foreground flex items-center gap-2">
                    <Icon name="MapPin" size={14} className="text-muted-foreground" />
                    {nearMiss?.location}
                  </p>
                </div>
              )}

              {/* Description */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t('description')}</p>
                <p className="text-sm text-foreground leading-relaxed bg-muted/30 rounded-lg p-3">
                  {nearMiss?.description}
                </p>
              </div>

              {/* Reported by */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Icon name="User" size={14} />
                  {nearMiss?.reporter?.full_name || 'Unknown'}
                </span>
                <span className="flex items-center gap-1.5">
                  <Icon name="Calendar" size={14} />
                  {nearMiss?.created_at ? new Date(nearMiss?.created_at)?.toLocaleDateString('en-GB') : '—'}
                </span>
              </div>

              {/* Photos */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('nearMissColPhotos')}</p>
                {photosLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    {t('nearMissLoadingPhotos')}
                  </div>
                ) : photos?.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('nearMissNoPhotos')}</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {photos?.map((photo, i) => (
                      <a key={photo?.id} href={photo?.url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={photo?.url}
                          alt={`Near miss photo ${i + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-border hover:opacity-90 transition-opacity"
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Status Update */}
              <div className="border-t border-border pt-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('nearMissUpdateStatus')}</p>
                <div className="flex gap-2">
                  <select
                    value={status}
                    onChange={e => setStatus(e?.target?.value)}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                  >
                    {STATUS_OPTIONS?.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleStatusUpdate}
                    disabled={updating || status === nearMiss?.status}
                  >
                    {updating ? <Icon name="Loader2" size={14} className="animate-spin" /> : t('nearMissUpdate')}
                  </Button>
                </div>
              </div>
            </>
          )}

          {activeTab === 'notes' && (
            <div>
              {companyId && nearMiss?.id && !nearMiss?._demo ? (
                <NotesFeed
                  entityType="near_miss"
                  entityId={nearMiss?.id}
                  companyId={companyId}
                  title="Near Miss Notes"
                />
              ) : (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  <Icon name="MessageSquare" size={28} className="mx-auto mb-2 text-muted-foreground/40" />
                  Notes are not available for demo records.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NearMissDetailModal;
